import { useEffect, useRef, useState } from "react";

export type WeightPoint = { time: number; value: number };

type Hover = { time: number; value: number; x: number; y: number };

const Y_GUTTER = 44;
const X_GUTTER = 28;
const PLOT_PAD_Y = 10;
const PLOT_PAD_X = 8;
const REVEAL_MS = 1400;

export function niceCeil(value: number) {
  if (!Number.isFinite(value) || value <= 0) return 10;
  const padded = value * 1.15;
  const steps = [5, 8, 10, 12, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 120, 150, 200, 250, 300];
  for (const step of steps) {
    if (step >= padded) return step;
  }
  const pow = 10 ** Math.floor(Math.log10(padded));
  return Math.ceil(padded / pow) * pow;
}

export function yTicks(max: number): number[] {
  if (max <= 0) return [0];
  const raw = max / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const n = raw / pow;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  const step = nice * pow;
  const out: number[] = [];
  for (let v = 0; v < max - step * 0.2; v += step) out.push(v);
  out.push(max);
  return out;
}

function drawSpline(ctx: CanvasRenderingContext2D, pts: [number, number][]) {
  if (pts.length < 2) return;
  if (pts.length === 2) {
    ctx.lineTo(pts[1][0], pts[1][1]);
    return;
  }
  const n = pts.length;
  const delta = new Array<number>(n - 1);
  const h = new Array<number>(n - 1);
  for (let i = 0; i < n - 1; i++) {
    h[i] = pts[i + 1][0] - pts[i][0];
    delta[i] = h[i] === 0 ? 0 : (pts[i + 1][1] - pts[i][1]) / h[i];
  }
  const m = new Array<number>(n);
  m[0] = delta[0];
  m[n - 1] = delta[n - 2];
  for (let i = 1; i < n - 1; i++) {
    m[i] = delta[i - 1] * delta[i] <= 0 ? 0 : (delta[i - 1] + delta[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (delta[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
    } else {
      const a = m[i] / delta[i];
      const b = m[i + 1] / delta[i];
      const s2 = a * a + b * b;
      if (s2 > 9) {
        const s = 3 / Math.sqrt(s2);
        m[i] = s * a * delta[i];
        m[i + 1] = s * b * delta[i];
      }
    }
  }
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const dx = h[i] / 3;
    ctx.bezierCurveTo(p0[0] + dx, p0[1] + m[i] * dx, p1[0] - dx, p1[1] - m[i + 1] * dx, p1[0], p1[1]);
  }
}

function yAtX(pts: [number, number][], x: number): number {
  if (x <= pts[0][0]) return pts[0][1];
  const last = pts[pts.length - 1];
  if (x >= last[0]) return last[1];
  for (let i = 1; i < pts.length; i++) {
    if (x <= pts[i][0]) {
      const span = Math.max(pts[i][0] - pts[i - 1][0], 1e-6);
      const u = (x - pts[i - 1][0]) / span;
      return pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * u;
    }
  }
  return last[1];
}

function fmtTick(v: number) {
  return Number.isInteger(v) ? String(v) : v.toFixed(0);
}

export function WeightChart({
  points,
  yMax,
  leftTime,
  rightTime,
  average,
  formatTime,
  onHover,
  seriesKey = "",
}: {
  points: WeightPoint[];
  yMax: number;
  leftTime: number;
  rightTime: number;
  average?: number;
  formatTime: (t: number) => string;
  onHover?: (point: WeightPoint | null) => void;
  seriesKey?: string;
}) {
  const plotRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hoverTRef = useRef<number | null>(null);
  const targetRef = useRef({ yMax, leftTime, rightTime });
  const camRef = useRef({ yMax, leftTime, rightTime });
  const pointsRef = useRef(points);
  const avgRef = useRef(average);
  const hoverKeyRef = useRef<string | null>(null);
  const onHoverRef = useRef(onHover);
  const seriesKeyRef = useRef(seriesKey);
  const activeSeriesRef = useRef<string | null>(null);
  const revealRef = useRef(0);
  const lastFrameRef = useRef(0);

  const [hover, setHover] = useState<Hover | null>(null);
  const [plotSize, setPlotSize] = useState({ w: 0, h: 0 });

  targetRef.current = { yMax, leftTime, rightTime };
  pointsRef.current = points;
  avgRef.current = average;
  onHoverRef.current = onHover;
  seriesKeyRef.current = seriesKey;

  useEffect(() => {
    const canvas = canvasRef.current;
    const plot = plotRef.current;
    if (!canvas || !plot) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let raf = 0;
    let running = true;
    let w = 0;
    let h = 0;

    const resize = () => {
      const rect = plot.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(1, Math.floor(rect.width));
      h = Math.max(1, Math.floor(rect.height));
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      setPlotSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };

    const ro = new ResizeObserver(resize);
    ro.observe(plot);
    resize();

    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      hoverTRef.current = Math.min(1, Math.max(0, x / Math.max(w, 1)));
    };
    const onLeave = () => {
      hoverTRef.current = null;
    };
    canvas.addEventListener("pointerdown", onMove);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("pointercancel", onLeave);

    const toX = (time: number, left: number, right: number) => {
      const span = Math.max(1, right - left);
      return PLOT_PAD_X + ((time - left) / span) * (w - PLOT_PAD_X * 2);
    };
    const toY = (value: number, max: number) => {
      const chartH = h - PLOT_PAD_Y * 2;
      return PLOT_PAD_Y + (1 - value / Math.max(max, 1)) * chartH;
    };

    const nearest = (time: number): WeightPoint | null => {
      const pts = pointsRef.current;
      if (!pts.length) return null;
      let best = pts[0];
      let bestD = Math.abs(pts[0].time - time);
      for (let i = 1; i < pts.length; i++) {
        const d = Math.abs(pts[i].time - time);
        if (d < bestD) {
          best = pts[i];
          bestD = d;
        }
      }
      return best;
    };

    const draw = (now: number) => {
      if (!running) return;
      const dt = lastFrameRef.current ? Math.min(now - lastFrameRef.current, 40) : 16.67;
      lastFrameRef.current = now;

      const key = seriesKeyRef.current;
      if (activeSeriesRef.current !== key) {
        activeSeriesRef.current = key;
        revealRef.current = 0;
        camRef.current.leftTime = targetRef.current.leftTime;
        camRef.current.rightTime = targetRef.current.rightTime;
        camRef.current.yMax = targetRef.current.yMax;
      }

      if (reduced) {
        revealRef.current = 1;
      } else if (w >= 16) {
        revealRef.current = Math.min(1, revealRef.current + dt / REVEAL_MS);
      }

      const reveal = revealRef.current;
      const cam = camRef.current;
      const span = Math.max(1, cam.rightTime - cam.leftTime);
      const clipW = w * reveal;

      ctx.clearRect(0, 0, w, h);

      const ticks = yTicks(targetRef.current.yMax);
      for (const tick of ticks) {
        const y = toY(tick, cam.yMax);
        if (y < -0.5 || y > h + 0.5) continue;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        if (tick === 0) {
          ctx.strokeStyle = "rgba(17,17,17,0.28)";
          ctx.setLineDash([]);
          ctx.lineWidth = 1;
        } else {
          ctx.strokeStyle = "rgba(17,17,17,0.08)";
          ctx.setLineDash([1, 3]);
          ctx.lineWidth = 1;
        }
        ctx.stroke();
      }
      ctx.setLineDash([]);

      if (avgRef.current != null && avgRef.current > 0) {
        const y = toY(avgRef.current, cam.yMax);
        ctx.beginPath();
        ctx.setLineDash([3, 4]);
        ctx.moveTo(0, y);
        ctx.lineTo(clipW, y);
        ctx.strokeStyle = "rgba(17,17,17,0.28)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);
      }

      const data = pointsRef.current;
      const visible = data.filter(
        (p) => p.time >= cam.leftTime - span * 0.02 && p.time <= cam.rightTime + span * 0.02,
      );

      if (visible.length >= 1) {
        const pts: [number, number][] = visible.map((p) => [
          toX(p.time, cam.leftTime, cam.rightTime),
          toY(p.value, cam.yMax),
        ]);

        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, Math.max(clipW, 0), h);
        ctx.clip();

        if (pts.length > 1) {
          const lastY = pts[pts.length - 1][1];
          const bandTop = Math.max(0, lastY - 28);
          ctx.beginPath();
          ctx.moveTo(pts[0][0], pts[0][1] + 18);
          ctx.lineTo(pts[0][0], pts[0][1]);
          drawSpline(ctx, pts);
          ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1] + 18);
          ctx.closePath();
          const glow = ctx.createLinearGradient(0, bandTop, 0, lastY + 22);
          glow.addColorStop(0, "rgba(17,17,17,0.10)");
          glow.addColorStop(1, "rgba(17,17,17,0)");
          ctx.fillStyle = glow;
          ctx.fill();
        }

        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        if (pts.length === 1) ctx.lineTo(pts[0][0], pts[0][1]);
        else drawSpline(ctx, pts);
        ctx.strokeStyle = "#111111";
        ctx.lineWidth = 2;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.stroke();
        ctx.restore();

        if (visible.length <= 16) {
          for (const [x, y] of pts) {
            if (x > clipW + 0.5) break;
            ctx.beginPath();
            ctx.arc(x, y, 2.4, 0, Math.PI * 2);
            ctx.fillStyle = "#111111";
            ctx.fill();
          }
        }

        const last = pts[pts.length - 1];
        const headX = reveal >= 1 ? last[0] : Math.min(Math.max(clipW, pts[0][0]), last[0]);
        const headY = reveal >= 1 ? last[1] : yAtX(pts, headX);
        const showHead = reveal > 0.02 && clipW >= pts[0][0] - 1;

        if (showHead) {
          if (reveal >= 1) {
            const pulseT = (now % 1600) / 1000;
            if (!reduced && pulseT < 1) {
              ctx.beginPath();
              ctx.arc(headX, headY, 6 + pulseT * 10, 0, Math.PI * 2);
              ctx.strokeStyle = `rgba(17,17,17,${0.22 * (1 - pulseT)})`;
              ctx.lineWidth = 1.25;
              ctx.stroke();
            }
          }
          ctx.beginPath();
          ctx.arc(headX, headY, 4.5, 0, Math.PI * 2);
          ctx.fillStyle = "#ffffff";
          ctx.fill();
          ctx.beginPath();
          ctx.arc(headX, headY, 2.6, 0, Math.PI * 2);
          ctx.fillStyle = "#111111";
          ctx.fill();
        }
      }

      const hoverT = hoverTRef.current;
      if (hoverT != null && data.length && reveal >= 1) {
        const time = cam.leftTime + hoverT * span;
        const point = nearest(time);
        if (point) {
          const x = toX(point.time, cam.leftTime, cam.rightTime);
          const y = toY(point.value, cam.yMax);
          ctx.beginPath();
          ctx.setLineDash([3, 3]);
          ctx.moveTo(x, 0);
          ctx.lineTo(x, h);
          ctx.strokeStyle = "rgba(17,17,17,0.18)";
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.arc(x, y, 3.5, 0, Math.PI * 2);
          ctx.fillStyle = "#111111";
          ctx.fill();

          const hoverKey = `${point.time}:${point.value.toFixed(1)}`;
          if (hoverKeyRef.current !== hoverKey) {
            hoverKeyRef.current = hoverKey;
            setHover({ time: point.time, value: point.value, x, y });
            onHoverRef.current?.(point);
          }
        }
      } else if (hoverKeyRef.current != null) {
        hoverKeyRef.current = null;
        setHover(null);
        onHoverRef.current?.(null);
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener("pointerdown", onMove);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("pointercancel", onLeave);
    };
  }, []);

  const ticks = yTicks(yMax);
  const span = Math.max(1, rightTime - leftTime);
  const xCount = plotSize.w > 0 && plotSize.w < 360 ? 3 : 4;
  const xLabels = Array.from({ length: xCount }, (_, i) => {
    const t = leftTime + (span * i) / (xCount - 1);
    const inner = plotSize.w ? plotSize.w - PLOT_PAD_X * 2 : 1;
    const pct = plotSize.w
      ? ((PLOT_PAD_X + (i / (xCount - 1)) * inner) / plotSize.w) * 100
      : (i / (xCount - 1)) * 100;
    return { t, pct };
  });

  const chartH = Math.max(1, plotSize.h - PLOT_PAD_Y * 2);
  const yPos = (value: number) =>
    plotSize.h ? ((PLOT_PAD_Y + (1 - value / yMax) * chartH) / plotSize.h) * 100 : (1 - value / yMax) * 100;

  const avgPct = average != null && yMax > 0 ? yPos(average) : null;
  const avgCollides =
    avgPct != null &&
    ticks.some((tick) => Math.abs(yPos(tick) - avgPct) < 6);

  const hoverX =
    hover && plotSize.w
      ? PLOT_PAD_X + ((hover.time - leftTime) / span) * (plotSize.w - PLOT_PAD_X * 2)
      : 0;
  const hoverY =
    hover && yMax > 0 && plotSize.h
      ? PLOT_PAD_Y + (1 - hover.value / yMax) * chartH
      : 0;
  const tipLeft = hover
    ? Math.min(Math.max(hoverX + 10, 8), Math.max(8, plotSize.w - 118))
    : 0;
  const tipTop = hover ? (hoverY - 44 < 8 ? hoverY + 12 : hoverY - 44) : 0;

  return (
    <div
      className="grid h-full w-full"
      style={{
        gridTemplateColumns: `${Y_GUTTER}px minmax(0, 1fr)`,
        gridTemplateRows: `minmax(0, 1fr) ${X_GUTTER}px`,
      }}
    >
      <div className="relative min-h-0">
        {ticks.map((tick) => {
          const pct = yPos(tick);
          return (
            <span
              key={tick}
              className="absolute right-2 text-[10px] tabular-nums leading-none text-faint"
              style={{
                top: `${pct}%`,
                transform: "translateY(-50%)",
              }}
            >
              {fmtTick(tick)}
            </span>
          );
        })}
        {avgPct != null && !avgCollides && (
          <span
            className="absolute right-2 text-[10px] leading-none text-muted"
            style={{ top: `${avgPct}%`, transform: "translateY(-50%)" }}
          >
            avg
          </span>
        )}
      </div>

      <div ref={plotRef} className="relative min-h-0 min-w-0 overflow-hidden">
        <canvas
          ref={canvasRef}
          className="block h-full w-full touch-none"
          style={{ cursor: "crosshair" }}
        />
        {hover && (
          <div
            className="pointer-events-none absolute z-10 rounded-sm border border-line bg-bg px-2 py-1.5 shadow-sm"
            style={{ left: tipLeft, top: tipTop }}
          >
            <div className="text-[11px] font-medium tabular-nums leading-none text-fg">
              {hover.value.toFixed(1)} kg
            </div>
            <div className="mt-1 text-[10px] leading-none text-faint">
              {formatTime(hover.time)}
            </div>
          </div>
        )}
      </div>

      <div className="relative">
        <span className="absolute right-2 top-2 text-[10px] leading-none text-faint">kg</span>
      </div>

      <div className="relative">
        {xLabels.map((lab, i) => {
          const align =
            i === 0 ? "left" : i === xLabels.length - 1 ? "right" : "center";
          const origin = i === 0 ? "0%" : i === xLabels.length - 1 ? "100%" : "50%";
          return (
            <span
              key={lab.t}
              className="absolute top-2 whitespace-nowrap text-[10px] tabular-nums leading-none text-faint"
              style={{
                left: `${lab.pct}%`,
                transform: `translateX(-${origin})`,
                textAlign: align,
              }}
            >
              {formatTime(lab.t)}
            </span>
          );
        })}
      </div>
    </div>
  );
}
